import type {AlertColor, AlertPropsColorOverrides} from "@mui/material";
import type {OverridableStringUnion} from "@mui/types";
import {useQuery} from "@tanstack/react-query";
import moment from "moment";
import React, {type ReactNode, useCallback} from "react";
import {
    clearProgramStatusCache,
    type SectionStatus,
    type SectionStatusMap,
} from "@/hooks/Program/useProgramStatus.ts";
import {apiEndpoint, jwtContext} from "./JWTProvider";

export const STATUS_CLOSED = "closed";
export const STATUS_OPEN = "open";
export const STATUS_WAITLIST = "waitlist";
export const STATUS_CLOSED_NO_WAITLIST = "closed-no-waitlist";
export const STATUS_REGISTERED = "registered";
export const STATUS_WAITLISTED = "waitlisted";
export const STATUS_PARTIAL = "partial";
export const STATUS_DUE = "due";
export const STATUS_PAID = "paid";
export const STATUS_REGISTERED_WITH_CREDITS = "registered-with-credits";
export const STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT =
    "registered-with-credits-pending-payment";
export const OVERLAP_MESSAGE =
    "This course time(s) overlaps with a previously registered course.  Please check your schedule and try again.";
export const MULTIPLE_REGISTRATION_MESSAGE = "You can only register for one section per term.";
export const ERROR_REGISTERING = "There was an error registering you for the course.";
export const ERROR_LOADING_PROGRAMS = "There was an error loading the list of programs.";

export type ProgramCourseStatus =
    | typeof STATUS_OPEN
    | typeof STATUS_CLOSED_NO_WAITLIST
    | typeof STATUS_WAITLIST
    | typeof STATUS_PARTIAL
    | typeof STATUS_REGISTERED
    | typeof STATUS_WAITLISTED
    | typeof STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT
    | typeof STATUS_REGISTERED_WITH_CREDITS
    | typeof STATUS_CLOSED;

export type ProgramCourseSchool = {
    shortName: string;
    fullName: string;
    id: string;
};

export type CourseQuestion = {
    id: string;
    active: boolean;
    answerList: string[] | null;
    question: string;
    responseType: string;
    userSubmittedAnswer: string | null;
    sortOrder: number | null;
};

export const ALERT_ERROR = "error";
export const ALERT_SUCCESS = "success";

export type AlertVariant = typeof ALERT_ERROR | typeof ALERT_SUCCESS;

type ProgramsState = {
    loading: boolean;
    showAlert: boolean;
    message: string;
    alertVariant: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
};

export type SurveyAnswers = {
    isAtRisk: boolean;
};

type Props = {
    children?: ReactNode;
};

type ProgramsProviderContextValue = {
    programsState: ProgramsState;
    performRegistrationAction:
        | ((
              program: Program,
              activeCourse: CourseSection,
              sectionStatus: SectionStatus,
              sectionStatusMap: SectionStatusMap,
              reason?: string,
          ) => Promise<boolean>)
        | null;
    programList: {
        loading: boolean;
        programs: Program[];
    };
    clearRegistrationError: (() => Promise<void>) | null;
    answerSurvey: ((surveyAnswers: SurveyAnswers, program: Program) => Promise<boolean>) | null;
    canPerformRegistrationAction:
        | ((
              activeCourse: CourseSection,
              program: Program,
              sectionStatusMap: SectionStatusMap,
          ) => boolean)
        | null;
    resetRegistrationStatus: ((course: CourseSection) => Promise<void>) | null;
    showErrorMessage: (message: string) => void;
};

export const ProgramsProviderContext = React.createContext<ProgramsProviderContextValue>({
    programsState: {
        loading: true,
        showAlert: false,
        message: "",
        alertVariant: "success",
    },
    programList: {
        loading: true,
        programs: [],
    },
    performRegistrationAction: null,
    clearRegistrationError: null,
    answerSurvey: null,
    canPerformRegistrationAction: null,
    resetRegistrationStatus: null,
    showErrorMessage: () => {},
});

const programsReducer = (state: ProgramsState, action: any): ProgramsState => {
    switch (action.type) {
        case "LOADING": {
            return {...state, loading: true, showAlert: false, alertVariant: "success"};
        }
        case "SET_PROGRAMS": {
            return {
                ...state,
                loading: false,
                showAlert: action.showAlert,
                alertVariant: action.alertVariant,
                message: action.message,
            };
        }
        case "REGISTRATION_ERROR": {
            return {
                ...state,
                loading: false,
                showAlert: action.showAlert,
                message: action.message,
                alertVariant: action.alertVariant,
            };
        }
        case "CLEAR_REGISTRATION_ERROR": {
            return {
                ...state,
                loading: false,
                showAlert: false,
                message: "",
                alertVariant: "success",
            };
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
};

const csOverlap = (c: CourseSection, cs: CourseSection) => {
    const csMeetingTimeStart = moment(cs.dateStartFull).format("HH:mm");
    const csMeetingTimeEnd = moment(cs.dateEndFull).format("HH:mm");

    const cMeetingTimeStart = moment(c.dateStartFull).format("HH:mm");
    const cMeetingTimeEnd = moment(c.dateEndFull).format("HH:mm");

    if (
        csMeetingTimeStart === "00:00:00" ||
        csMeetingTimeEnd === "00:00:00" ||
        cMeetingTimeStart === "00:00:00" ||
        cMeetingTimeEnd === "00:00:00"
    ) {
        return false;
    }

    if (
        !(
            (csMeetingTimeStart >= cMeetingTimeStart && csMeetingTimeStart <= cMeetingTimeEnd) ||
            (csMeetingTimeEnd >= cMeetingTimeStart && csMeetingTimeEnd <= cMeetingTimeEnd)
        )
    ) {
        return false;
    }

    const cDOW = c.meetingDays?.split(",") ?? [];
    const csDOW = cs.meetingDays?.split(",") ?? [];
    if (!cDOW.some(d => csDOW.includes(d))) {
        return false;
    }

    const cStart = moment(c.dateStartFull, "YYYY-MM-DD");
    const cEnd = moment(c.dateEndFull, "YYYY-MM-DD");
    const csStart = moment(cs.dateStartFull, "YYYY-MM-DD");
    const csEnd = moment(cs.dateEndFull, "YYYY-MM-DD");

    return (csStart >= cStart && csStart <= cEnd) || (csEnd >= cStart && csEnd <= cEnd);
};

export type CourseSection = {
    programCancellationMessage: string;
    category: string | null;
    courseNumber: string | null;
    description: string | null;
    dates: string;
    meetingDays: string;
    meetingTime: string;
    dateStartFull: string;
    dateEndFull: string;
    credits: number | null;
    name: string;
    courseUUID: string;
    courseSelectionUUID: string;
    deliveryMethod: string | null;
    school: {
        shortName: string;
        fullName: string;
        id: number;
    };
    costFree: number | null;
    cost: number | null;
    costReduced: number | null;
    sectionNumber: number | null;
    term: string;
    termID: string;
    oneSectionPerTermOverride: boolean;
    timeConflictOverride: boolean;
    allowCommunity: number | null;
    requiresPayment: boolean;
    notes: string | null;
    programAllowPartial: boolean;
    courseQuestions: CourseQuestion[] | null;
};

export type Program = {
    programUUID: string;
    name: string;
    open: boolean;
    oneSectionPerTerm: boolean;
    programCancellationMessage: string;
    requiresSurvey: boolean | null;
    courses: CourseSection[];
};

const ProgramsProvider: React.FC<Props> = ({children}: Props) => {
    const user = React.useContext(jwtContext);
    const {data: programList, isPending: listIsPending} = useQuery({
        initialData: [],
        refetchOnWindowFocus: false,
        queryKey: ["programList", user?.studentId],
        queryFn: async () => {
            if (!user) {
                return [];
            }

            const url = new URL("/v1/program/list", apiEndpoint);
            const response = await user.apiFetch(url.toString(), {
                signal: AbortSignal.timeout(10000), //10 second timeout
            });

            return (await response.json()) as Program[];
        },
    });
    const [programsState, setProgramsState] = React.useReducer(programsReducer, {
        loading: true,
        showAlert: false,
        message: "",
        alertVariant: "success",
    });

    const clearRegistrationError = useCallback(async () => {
        setProgramsState({type: "CLEAR_REGISTRATION_ERROR"});
    }, []);

    const resetRegistrationStatus = useCallback(async (course: CourseSection) => {
        setProgramsState({type: "SWITCH_PAYMENT", course: course}); //TODO: clear cache
    }, []);

    const answerSurvey = useCallback(
        async (surveyAnswers: SurveyAnswers, program: Program) => {
            if (!user) {
                return false;
            }

            const url = new URL(`/v1/survey/${program.programUUID}`, apiEndpoint);
            const responseRegistrationAction = await user.apiFetch(url.toString(), {
                method: "POST",
                body: JSON.stringify(surveyAnswers),
            });
            if (responseRegistrationAction.status !== 200) {
                setProgramsState({
                    type: "REGISTRATION_ERROR",
                    showAlert: true,
                    message: "Unable to save survey",
                    alertVariant: ALERT_ERROR,
                });
                return false;
            }

            await clearProgramStatusCache(user.id);
            setProgramsState({
                type: "SET_PROGRAMS",
                showAlert: true,
                message: "Successfully submitted Survey",
                alertVariant: ALERT_SUCCESS,
            });
            return true;
        },
        [user],
    );

    const canPerformRegistrationAction = (
        activeCourse: CourseSection,
        program: Program,
        sectionStatusMap: SectionStatusMap,
    ): boolean => {
        const overlap = hasOverlap(activeCourse, program, sectionStatusMap);

        if (overlap) {
            setProgramsState({
                type: "REGISTRATION_ERROR",
                showAlert: true,
                message: OVERLAP_MESSAGE,
                alertVariant: ALERT_ERROR,
            });
        }

        return !overlap;
    };

    const hasMultipleRegistrationViolation = useCallback(
        (
            activeCourse: CourseSection,
            program: Program,
            sectionStatus: SectionStatus,
            sectionStatusMap: SectionStatusMap,
        ): boolean => {
            const isWaitlist = sectionStatus.status === STATUS_WAITLIST;

            if (
                [STATUS_OPEN, STATUS_WAITLIST].includes(sectionStatus.status) &&
                !activeCourse.timeConflictOverride
            ) {
                if (
                    program.courses.some(r => {
                        if (isWaitlist || r.timeConflictOverride) {
                            return false;
                        }
                        if (
                            program.oneSectionPerTerm &&
                            !activeCourse.oneSectionPerTermOverride &&
                            r.courseUUID === activeCourse.courseUUID &&
                            r.termID === activeCourse.termID
                        ) {
                            return (
                                sectionStatusMap.get(r.courseSelectionUUID)?.status ===
                                STATUS_REGISTERED
                            );
                        }
                        return false;
                    })
                ) {
                    return true;
                }
            }

            return false;
        },
        [],
    );

    const hasOverlap = useCallback(
        (
            activeCourse: CourseSection,
            program: Program,
            sectionStatusMap: SectionStatusMap,
        ): boolean => {
            const activeCourseIndex = program.courses.findIndex(
                course => course.courseSelectionUUID === activeCourse.courseSelectionUUID,
            );
            const courseToRegister = program.courses[activeCourseIndex];
            let overlap = false;

            if (
                [STATUS_OPEN, STATUS_WAITLIST].includes(
                    sectionStatusMap.get(activeCourse.courseSelectionUUID)?.status ?? "",
                ) &&
                !courseToRegister.timeConflictOverride
            ) {
                if (
                    program.courses.some(r => {
                        return (
                            sectionStatusMap.get(r.courseSelectionUUID)?.status ===
                                STATUS_REGISTERED && csOverlap(r, courseToRegister)
                        );
                    })
                ) {
                    overlap = true;
                }
            }

            return overlap;
        },
        [],
    );

    const performRegistrationAction = useCallback(
        async (
            program: Program,
            activeCourse: CourseSection,
            sectionStatus: SectionStatus,
            sectionStatusMap: SectionStatusMap,
            reason?: string,
        ): Promise<boolean> => {
            if (!user) {
                return false;
            }

            let actionUrl = "";
            let method = "";
            const overlap =
                sectionStatus.status !== STATUS_WAITLIST &&
                hasOverlap(activeCourse, program, sectionStatusMap);
            const multipleRegistrationError = hasMultipleRegistrationViolation(
                activeCourse,
                program,
                sectionStatus,
                sectionStatusMap,
            );

            if (multipleRegistrationError) {
                setProgramsState({
                    type: "REGISTRATION_ERROR",
                    showAlert: true,
                    message: MULTIPLE_REGISTRATION_MESSAGE,
                    alertVariant: ALERT_ERROR,
                });
                return false;
            }
            if (overlap) {
                setProgramsState({
                    type: "REGISTRATION_ERROR",
                    showAlert: true,
                    message: OVERLAP_MESSAGE,
                    alertVariant: ALERT_ERROR,
                });
                return false;
            }

            if (sectionStatus.status === STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT) {
                sectionStatus.status = STATUS_REGISTERED_WITH_CREDITS;
                setProgramsState({
                    type: "SET_PROGRAMS",
                    showAlert: true,
                    message:
                        "Your Payment has been received and your course credits have been applied.",
                    alertVariant: ALERT_SUCCESS,
                });
                return true;
            }

            switch (sectionStatus.status) {
                case STATUS_REGISTERED:
                    actionUrl = `/${activeCourse.courseSelectionUUID}`;
                    method = "DELETE";
                    break;
                case STATUS_REGISTERED_WITH_CREDITS:
                    actionUrl = `/${activeCourse.courseSelectionUUID}`;
                    method = "DELETE";
                    break;
                case STATUS_WAITLISTED:
                    actionUrl = `/waitlist/${activeCourse.courseSelectionUUID}`;
                    method = "DELETE";
                    break;
                case STATUS_WAITLIST:
                    actionUrl = `/waitlist/${activeCourse.courseSelectionUUID}`;
                    method = "POST";
                    break;
                case STATUS_OPEN:
                default:
                    actionUrl = `/${activeCourse.courseSelectionUUID}`;
                    method = "POST";
                    break;
            }

            const url = new URL(`/v1/register${actionUrl}`, apiEndpoint);

            const init: RequestInit = {
                method: method,
                signal: AbortSignal.timeout(10000), //10 second timeout
            };

            init.body = JSON.stringify({});
            if (activeCourse.courseQuestions) {
                init.body = JSON.stringify({
                    activeCourse: activeCourse,
                    reason,
                });
            } else {
                init.body = JSON.stringify({
                    reason,
                });
            }

            let responseJSON = null;
            let responseStatus = 600;
            let fetchError: boolean | string = false;

            try {
                const responseRegistrationAction = await user.apiFetch(url.toString(), init);
                responseStatus = responseRegistrationAction.status;
                responseJSON = await responseRegistrationAction.json();
            } catch (e) {
                if (e instanceof SyntaxError) {
                    fetchError = "Invalid response from server. Please try again.";
                    console.error("syntax error", e);
                } else if (e instanceof Error) {
                    fetchError = e.message;
                } else {
                    fetchError = `Unknown Error from server. ${e}`;
                    console.error("error", e);
                }
                console.error("error registering", e);
            }

            if (!responseJSON) {
                setProgramsState({
                    type: "REGISTRATION_ERROR",
                    showAlert: true,
                    message: `${ERROR_REGISTERING} ${fetchError}`,
                    alertVariant: ALERT_ERROR,
                });
                return false;
            }

            if (responseStatus !== 200) {
                if (responseStatus === 406) {
                    //api says pull down the programs again
                    try {
                        await clearProgramStatusCache(user.id);
                    } catch (e) {
                        console.error("error forced refresh", e);
                        setProgramsState({
                            type: "REGISTRATION_ERROR",
                            showAlert: true,
                            message: `ERROR_REGISTERING ${fetchError}`,
                            alertVariant: ALERT_ERROR,
                        });
                        return false;
                    }
                }

                let errorMessage = "";

                switch (sectionStatus.status) {
                    case STATUS_OPEN:
                        errorMessage = "Unable to register for event.";
                        break;
                    case STATUS_REGISTERED:
                        errorMessage = "Unable to cancel registration.";
                        break;
                    case STATUS_REGISTERED_WITH_CREDITS:
                        errorMessage = "Unable to cancel registration.";
                        break;
                    case STATUS_WAITLISTED:
                        errorMessage = "Unable to cancel waitlist.";
                        break;
                    case STATUS_WAITLIST:
                        errorMessage = "Unable to process waitlist request.";
                        break;
                    case STATUS_CLOSED_NO_WAITLIST:
                        errorMessage = "This course has filled up.";
                        break;
                }

                if (responseJSON.hint) {
                    errorMessage += " " + responseJSON.hint;
                }

                setProgramsState({
                    type: "REGISTRATION_ERROR",
                    showAlert: true,
                    message: errorMessage,
                    alertVariant: ALERT_ERROR,
                });
                return false;
            }

            let message: string;

            switch (sectionStatus.status) {
                case STATUS_REGISTERED:
                    message = "Registration removed";
                    break;
                case STATUS_WAITLISTED:
                    message = "Waitlist removed";
                    break;
                case STATUS_WAITLIST:
                    message = "You have been added to the waitlist";
                    break;
                case STATUS_OPEN:
                default:
                    message = "Registration successful";
                    break;
            }

            await clearProgramStatusCache(user.id);
            setProgramsState({
                type: "SET_PROGRAMS",
                showAlert: true,
                message: message,
                alertVariant: ALERT_SUCCESS,
            });

            return true;
        },
        [setProgramsState, hasOverlap],
    );

    const showErrorMessage = (message: string) => {
        setProgramsState({
            type: "REGISTRATION_ERROR",
            showAlert: true,
            message: message,
            alertVariant: ALERT_ERROR,
        });
    };

    return (
        <ProgramsProviderContext.Provider
            value={{
                programList: {
                    loading: listIsPending,
                    programs: programList ?? [],
                },
                programsState,
                performRegistrationAction,
                clearRegistrationError,
                answerSurvey,
                canPerformRegistrationAction,
                resetRegistrationStatus,
                showErrorMessage,
            }}
        >
            {children}
        </ProgramsProviderContext.Provider>
    );
};

export default ProgramsProvider;
